import { useCallback, useEffect, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Conversation } from "@zapir/shared";
import { api, type Page, type TimelineMessage } from "@/lib/api";
import { cacheMessages, readMessages, saveMediaBlob } from "@/lib/db";
import { qk } from "@/lib/query";
import { toast, useSession } from "@/lib/store";
import { asApiError } from "@/lib/errors";
import { ensureMediaId, newClientMessageId, useOutbox, type OutboxItem } from "@/lib/outbox";
import { registerLocalMedia, type UploadableKind } from "@/lib/media";

type MessagePage = Page<TimelineMessage>;

export function useMessages(conversationId: string) {
  const query = useInfiniteQuery({
    queryKey: qk.messages(conversationId),
    queryFn: ({ pageParam }) => api.messages(conversationId, pageParam, 30),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: MessagePage) =>
      last.pageInfo.hasMore ? (last.pageInfo.nextCursor ?? undefined) : undefined,
    enabled: Boolean(conversationId),
  });

  useEffect(() => {
    if (query.data) {
      const all = query.data.pages.flatMap((p) => p.items);
      void cacheMessages(conversationId, all);
    }
  }, [query.data, conversationId]);

  return query;
}

/** Historico em cache para leitura offline (RF-UX-050). */
export function useCachedMessages(conversationId: string): { items: TimelineMessage[]; loading: boolean } {
  const [state, setState] = useState<{ items: TimelineMessage[]; loading: boolean }>({ items: [], loading: true });
  useEffect(() => {
    let alive = true;
    void readMessages(conversationId).then((items) => {
      if (alive) setState({ items: items ?? [], loading: false });
    });
    return () => {
      alive = false;
    };
  }, [conversationId]);
  return state;
}

/** Insere/reconcilia uma mensagem no cache, deduplicando (EC-UX-021/053). */
export function upsertMessage(qc: QueryClient, message: TimelineMessage): void {
  qc.setQueryData<{ pages: MessagePage[]; pageParams: unknown[] }>(qk.messages(message.conversationId), (data) => {
    if (!data) return data;
    const pages = data.pages.map((page, index) => {
      const items = [...page.items];
      const byId = items.findIndex((m) => m.id === message.id);
      const byClient = message.clientMessageId
        ? items.findIndex((m) => m.clientMessageId === message.clientMessageId)
        : -1;
      const target = byId >= 0 ? byId : byClient;
      // MERGE, nao substituicao: eventos de atualizacao chegam parciais
      // (so `deliveryStatus`, so `mediaStatus`...) e trocar o objeto inteiro
      // apagava corpo, tipo e horario da bolha que ja estava na tela.
      if (target >= 0) items[target] = { ...items[target]!, ...message };
      // A pagina 0 e a mais nova (a timeline e invertida no ChatScreen), entao a
      // mensagem nova entra no INICIO dela para cair no fim do historico.
      else if (index === 0) items.unshift(message);
      return { ...page, items };
    });
    return { ...data, pages };
  });
}

function patchConversationPreview(qc: QueryClient, message: TimelineMessage): void {
  const preview = (c: Conversation): Conversation =>
    c.id === message.conversationId
      ? {
          ...c,
          lastMessagePreview: message.body,
          lastMessageAt: message.createdAt,
          lastMessageOutgoing: message.direction === "outbound",
        }
      : c;
  qc.setQueriesData<{ pages: Page<Conversation>[]; pageParams: unknown[] }>(
    { queryKey: ["conversations"] },
    (data) => {
      if (!data || !Array.isArray(data.pages)) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({ ...page, items: page.items.map(preview) })),
      };
    },
  );
}

export interface SendArgs {
  body: string;
  quotedMessageId?: string;
  isInternalNote: boolean;
  mentions?: string[];
}

export interface SendMediaArgs {
  file: Blob;
  fileName: string;
  mime: string;
  kind: UploadableKind;
  caption?: string;
  quotedMessageId?: string;
  /** Duração medida do áudio/voz em ms (gravador PTT). */
  durationMs?: number | null;
}

/**
 * "Apagar para todos". A mensagem NAO sai da timeline: volta do servidor com
 * `revokedAt` e o `upsertMessage` a substitui no cache, entao a bolha apenas
 * fica esmaecida.
 */
export function useDeleteMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.deleteMessage(conversationId, messageId),
    onSuccess: (message) => {
      upsertMessage(qc, message);
      toast({ tone: "success", message: "Mensagem apagada no WhatsApp." });
    },
    onError: (error) => {
      const apiError = asApiError(error);
      toast({ tone: "danger", message: apiError?.copy ?? "Não foi possível apagar." });
    },
  });
}

/**
 * Encaminha para varias conversas. Um destino que falha nao anula os outros —
 * o servidor devolve a contagem e a lista de falhas, e o aviso diz os dois.
 */
export function useForwardMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, conversationIds }: { messageId: string; conversationIds: string[] }) =>
      api.forwardMessage(conversationId, messageId, conversationIds),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["conversations"] });
      if (result.forwarded === 0) {
        toast({ tone: "danger", message: "Não foi possível encaminhar." });
        return;
      }
      const alvo = result.forwarded === 1 ? "1 conversa" : `${result.forwarded} conversas`;
      toast({
        tone: result.failed.length > 0 ? "info" : "success",
        message:
          result.failed.length > 0
            ? `Encaminhada para ${alvo}; ${result.failed.length} falhou.`
            : `Encaminhada para ${alvo}.`,
      });
    },
    onError: (error) => {
      const apiError = asApiError(error);
      toast({ tone: "danger", message: apiError?.copy ?? "Não foi possível encaminhar." });
    },
  });
}

export function useChatActions(conversationId: string) {
  const qc = useQueryClient();
  const tenantId = useSession((s) => s.tenantId);
  const add = useOutbox((s) => s.add);
  const remove = useOutbox((s) => s.remove);
  const setStatus = useOutbox((s) => s.setStatus);

  const deliver = useCallback(
    async (item: OutboxItem) => {
      if (!tenantId) return;
      setStatus(item.clientMessageId, "sending");
      try {
        let message: TimelineMessage;
        if (item.isInternalNote) {
          message = await api.sendNote({ conversationId: item.conversationId, body: item.body, mentions: item.mentions });
        } else if (item.mediaKind) {
          // Upload primeiro (com progresso na bolha), depois o envio com
          // `mediaId` + legenda. Offline: o upload falha com rede e o item
          // volta a pendente, preservado na fila IndexedDB.
          const mediaId = await ensureMediaId(tenantId, item);
          message = await api.sendMessage({
            conversationId: item.conversationId,
            mediaId,
            caption: item.body || undefined,
            clientMessageId: item.clientMessageId,
            quotedMessageId: item.quotedMessageId,
          });
        } else {
          message = await api.sendMessage({
            conversationId: item.conversationId,
            body: item.body,
            clientMessageId: item.clientMessageId,
            quotedMessageId: item.quotedMessageId,
          });
        }
        upsertMessage(qc, message);
        patchConversationPreview(qc, message);
        await remove(tenantId, item.clientMessageId);
        void qc.invalidateQueries({ queryKey: ["conversations"] });
      } catch (error) {
        const apiError = asApiError(error);
        if (apiError?.status === 0) {
          setStatus(item.clientMessageId, "pending");
        } else {
          setStatus(item.clientMessageId, "failed", apiError?.code);
          toast({
            tone: "danger",
            message: apiError?.copy ?? "Não foi possível enviar agora. O texto foi preservado.",
            actionLabel: "Tentar de novo",
            onAction: () => void deliver({ ...item, status: "failed" }),
          });
        }
      }
    },
    [qc, remove, setStatus, tenantId],
  );

  const send = useCallback(
    async ({ body, quotedMessageId, isInternalNote, mentions }: SendArgs) => {
      if (!tenantId || !body.trim()) return;
      const item: OutboxItem = {
        clientMessageId: newClientMessageId(),
        conversationId,
        body: body.trim(),
        quotedMessageId,
        isInternalNote,
        mentions,
        createdAt: Date.now(),
        status: "pending",
      };
      await add(tenantId, item);
      void deliver(item);
    },
    [add, conversationId, deliver, tenantId],
  );

  /**
   * Envio de mídia (docs/06): a bolha otimista aparece imediatamente com a
   * URL local (`URL.createObjectURL`); `deliver` faz upload+envio e troca
   * pela URL do servidor ao confirmar. O blob é persistido para retry e
   * fila offline.
   */
  const sendMedia = useCallback(
    async ({ file, fileName, mime, kind, caption, quotedMessageId, durationMs }: SendMediaArgs) => {
      if (!tenantId) return;
      const clientMessageId = newClientMessageId();
      registerLocalMedia(clientMessageId, file);
      await saveMediaBlob(clientMessageId, file);
      const item: OutboxItem = {
        clientMessageId,
        conversationId,
        body: (caption ?? "").trim(),
        quotedMessageId,
        isInternalNote: false,
        createdAt: Date.now(),
        status: "pending",
        mediaKind: kind,
        mediaName: fileName,
        mediaSize: file.size,
        mediaMime: mime || file.type,
        mediaDurationMs: durationMs ?? null,
      };
      await add(tenantId, item);
      void deliver(item);
    },
    [add, conversationId, deliver, tenantId],
  );

  const retry = useCallback((item: OutboxItem) => void deliver(item), [deliver]);

  return { send, sendMedia, retry };
}
